import axios from 'axios';

// Call the backend SAME-ORIGIN via the '/api' ingress/worker proxy. Using a relative
// base (instead of an absolute REACT_APP_BACKEND_URL) means requests work under ANY
// host the app is served from — preview, crew-leads.emergent.host, or the custom
// domain app.leadcrew.in — and the httpOnly refresh cookie stays first-party
// (no cross-site SameSite/CORS failures).
const API_PREFIX = '/api';

// Access token is kept ONLY in memory (never localStorage) to reduce XSS exposure.
// The refresh token lives in a secure httpOnly cookie managed by the backend.
let accessToken = null;
let onUnauthorized = null;

export const setAccessToken = (token) => { accessToken = token || null; };
export const getAccessToken = () => accessToken;
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

const api = axios.create({
  baseURL: API_PREFIX,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send/receive the httpOnly refresh cookie
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshCall) {
      originalRequest._retry = true;
      try {
        // De-dupe concurrent refreshes
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API_PREFIX}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        const resp = await refreshPromise;
        setAccessToken(resp.data.access_token);
        originalRequest.headers.Authorization = `Bearer ${resp.data.access_token}`;
        return api(originalRequest);
      } catch (refreshErr) {
        setAccessToken(null);
        if (onUnauthorized) onUnauthorized();
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
